/** Contrat HTTP de l’assistant ; lectures et gestes isolés par compte et galerie. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReglagesAssistant, TriAssistant, BrouillonAssistant } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { Message } from './boite-reception.js';

export type Reglages = ReglagesAssistant & { version: number };

export interface Relance {
  _id: string;
  type: 'conversation' | 'offre' | 'vernissage';
  objetId: string;
  conversationId?: string;
  contactId: string;
  proposeeLe: string;
  etat: 'proposee' | 'preparee';
  version: number;
}

export interface Essai {
  tri: TriAssistant;
  sensible: boolean;
  motifsSensibles: string[];
  brouillon: BrouillonAssistant | null;
  refus: string | null;
}

export interface TableauAssistant {
  jours: number;
  messagesTries: number;
  brouillonsPrepares: number;
  partSansModification: number | null;
  reponsesAutomatiques: number;
  conversationsSensibles: number;
  echecs: Record<string, number>;
}

const base = (g: string) => `/g/${encodeURIComponent(g)}/assistant-reponse`;

export const assistantApi = {
  reglages: (g: string, appel: AppelApi) =>
    api.get<Reglages>(`${base(g)}/reglages`, { galerie: g, ...appel }),
  regler: (g: string, r: Reglages & { confirmationAutomatique?: boolean }, appel: AppelApi) =>
    api.put<Reglages>(
      `${base(g)}/reglages`,
      {
        ...r,
        reponsesPratiques: { actif: r.reponsesPratiques.actif, sujets: r.reponsesPratiques.sujets },
      },
      { galerie: g, ...appel },
    ),
  conversation: (
    g: string,
    id: string,
    geste: 'trier' | 'brouillon' | 'lever-sensible',
    corps: { version: number; motif?: string },
    appel: AppelApi,
  ) =>
    api.post<{ message?: Message }>(
      `${base(g)}/conversations/${encodeURIComponent(id)}/${geste}`,
      corps,
      { galerie: g, ...appel },
    ),
  relances: (g: string, page: number, appel: AppelApi) =>
    api.get<{ relances: Relance[]; page: number; total: number }>(
      `${base(g)}/relances?page=${page}`,
      { galerie: g, ...appel },
    ),
  relance: (
    g: string,
    id: string,
    geste: 'preparer' | 'ecarter',
    version: number,
    appel: AppelApi,
  ) =>
    api.post<{ message?: Message }>(
      `${base(g)}/relances/${encodeURIComponent(id)}/${geste}`,
      { version },
      { galerie: g, ...appel },
    ),
  essai: (g: string, corps: { texte: string } | { conversationId: string }, appel: AppelApi) =>
    api.post<Essai>(`${base(g)}/essai`, corps, { galerie: g, ...appel }),
  tableau: (g: string, appel: AppelApi) =>
    api.get<TableauAssistant>(`${base(g)}/tableau`, { galerie: g, ...appel }),
};

export function useLectureAssistant<T>(
  g: string,
  cle: string,
  lire: (appel: AppelApi) => Promise<T>,
  page = 1,
) {
  return useQuery({
    queryKey: cleMetier(g, 'assistant-reponse', cle, page),
    queryFn: (c) => lire(appelDeRequete(c)),
  });
}

export function useActionAssistant<T, V>(
  g: string,
  action: (corps: V, appel: AppelApi) => Promise<T>,
) {
  const client = useQueryClient();

  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSettled: async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: cleMetier(g, 'assistant-reponse') }),
          client.invalidateQueries({ queryKey: cleMetier(g, 'boite-reception') }),
        ]);
      },
    }),
  );
}
